import { Styles } from '@types'
import { Modal, Form, Input, notification } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import dayjs from 'dayjs'
import { Cash } from 'gadjet-v2-types/dist/model'
import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'

import formRule from '@utils/formRule'

import CashAPI from '@apis/branch/cash'

import { RootState } from '@reducers/index'

import HiddenItems from '@components/atoms/Form/HiddenItems'
import InputPrice from '@components/atoms/InputPrice'
import LocalDatePicker from '@components/atoms/LocalDatePicker'
import Loading from '@components/molecules/Loading'

type Props = {
  hqId: number
  branchId: number
  cashId?: number
  visible: boolean
  onClose: () => void
  onDone: () => void
}

export default function CashFormModal({ hqId, branchId, cashId, visible, onClose, onDone }: Props): JSX.Element {
  const [form] = useForm<Cash>()
  const [loading, setLoading] = useState(false)
  const manager = useSelector((state: RootState) => state.auth.manager)

  const onOk = async () => {
    try {
      setLoading(true)
      const values = await form.validateFields()

      if (cashId) await CashAPI.updateCash({ hqId, branchId, cashId }, { cash: values })
      else await CashAPI.addCash({ hqId, branchId }, { cash: values })

      notification.success({ message: '저장되었습니다.' })

      setLoading(false)
      onDone()
      onClose()
    } catch (err) {
      setLoading(false)
    }
  }

  const reset = () => {
    form.resetFields()
  }

  const getCash = async () => {
    if (!cashId) return
    setLoading(true)
    const { data } = await CashAPI.getCashDetail({ hqId, branchId, cashId })
    form.setFieldsValue(data)
    setLoading(false)
  }

  useEffect(() => {
    if (visible) {
      reset()
      getCash()
    }
  }, [visible])

  const initialValues: Partial<Cash> = {
    date: dayjs().format('YYYY-MM-DD'),
    managerId: manager?.managerId,
    managerName: manager?.name,
    memo: '',
  }

  return (
    <Modal
      title="현금"
      okText="저장"
      onOk={onOk}
      confirmLoading={loading}
      visible={visible}
      onCancel={onClose}
      maskClosable={false}
    >
      <Loading loading={loading}>
        <Form form={form} layout="vertical" initialValues={initialValues}>
          <Form.Item
            label="날짜"
            name="date"
            getValueProps={(d) => ({ value: dayjs(d) })}
            getValueFromEvent={(_, d) => d}
            required
            rules={[formRule.required]}
          >
            <LocalDatePicker />
          </Form.Item>
          <Form.Item name="price" label="금액" rules={[formRule.required]}>
            <InputPrice addonAfter="원" />
          </Form.Item>
          <Form.Item name="memo" label="메모">
            <Input.TextArea style={styles.textarea} />
          </Form.Item>

          <HiddenItems names={['managerId', 'managerName']} />
        </Form>
      </Loading>
    </Modal>
  )
}

const styles: Styles = {
  textarea: { height: '200px' },
}
